"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MessageBox = exports.LeanMessageBox = void 0;
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
const feed_v2_models_1 = require("@forto-dev/feed-v2-models");
const frontend_infra_auth_1 = require("@forto-dev/frontend-infra-auth");
const frontend_infra_localisation_1 = require("@forto-dev/frontend-infra-localisation");
const frontend_infra_unleash_1 = require("@forto-dev/frontend-infra-unleash");
const silhouette_core_1 = require("@freighthub/silhouette-core");
const lodash_es_1 = require("lodash-es");
const moment_1 = tslib_1.__importDefault(require("moment"));
const react_1 = require("react");
const useAnalytics_1 = require("../../../hooks/useAnalytics");
const LoggerContext_1 = require("../../../lib/LoggerContext");
const Avatar_1 = require("../../../util/Avatar/Avatar");
const utils_1 = require("../../../util/utils");
const featureFlagKeys_1 = require("../../../types/featureFlagKeys");
const postFeedV2MessageApi_1 = require("../api/postFeedV2MessageApi");
const postMessageApi_1 = require("../api/postMessageApi");
const useRenderDocumentUpload_1 = require("../hooks/useRenderDocumentUpload");
const ActivityFeedContext_1 = require("./ActivityFeedContext");
const MessagesCollectionStore_1 = require("./MessagesCollectionStore");
var MessageType;
(function (MessageType) {
    MessageType["Communication"] = "communication";
    MessageType["Message"] = "message";
})(MessageType || (MessageType = {}));
const useStyles = (0, silhouette_core_1.makeStyles)((theme) => ({
    root: {
        background: theme.palette.background.paper,
        margin: theme.spacing(0, 1.5, 4, 1.5),
        paddingTop: theme.spacing(2.5),
    },
    container: {
        maxWidth: '660px',
        display: 'flex',
        margin: '0 auto',
    },
    avatarWrapper: {
        margin: theme.spacing(0, 4, 0, 0),
    },
    inputBoxContainer: {
        position: 'relative',
        width: '100%',
        marginRight: theme.spacing(1),
    },
    inputBoxInnerContainer: {
        background: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    inputBox: {
        minHeight: 171,
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1.5, 2),
        alignItems: 'normal',
    },
    actionsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        margin: theme.spacing(0, 0, 0.5, 2),
        gap: theme.spacing(1),
    },
    hintContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        textAlign: 'right',
        height: '38px',
        marginTop: theme.spacing(1.125),
    },
    hintText: {
        fontStyle: 'italic',
    },
    label: {
        position: 'absolute',
        top: '-10px',
        left: theme.spacing(1),
        background: theme.palette.background.paper,
        padding: theme.spacing(0, 0.5),
        color: theme.palette.text.primary,
    },
    internalNote: {
        marginRight: 'auto',
    },
}));
const maxMessageLength = 5000;
const debounceDelay = 500;
const LeanMessageBox = ({ avatar, avatarType, isForCustomer, storedMessage, saveMessageToStorage, deleteMessageFromStorage, onSendMessage, isLoading, isInternalActivitiesEnabled = false, isDocumentsUploadEnabled = false, uploadDocumentButton, userName, }) => {
    const { state: { user }, } = (0, frontend_infra_auth_1.useAuth)();
    const { translate } = (0, frontend_infra_localisation_1.useI18n)();
    const classes = useStyles();
    const [message, setMessage] = (0, react_1.useState)(storedMessage?.content ?? '');
    const [messageType, setMessageType] = (0, react_1.useState)(MessageType.Message);
    const isCustomerMessage = messageType === MessageType.Message;
    const [debounceHint, setDebounceHint] = (0, react_1.useState)(storedMessage?.content ? translate('activity-feed_message_saved') : '');
    const [debounceHintDate, setDebounceHintDate] = (0, react_1.useState)(storedMessage?.timestamp ? (0, moment_1.default)(storedMessage.timestamp) : null);
    const { uploadButton, uploadDialog } = (0, useRenderDocumentUpload_1.useRenderDocumentUpload)(isDocumentsUploadEnabled, uploadDocumentButton);
    const placeholder = (0, react_1.useMemo)(() => {
        if (!isForCustomer) {
            return isCustomerMessage
                ? translate('activity-feed_message_customer')
                : translate('activity-feed_note_freighthub');
        }
        return translate('activity-feed_message_upload_document');
    }, [isCustomerMessage, isForCustomer, translate]);
    const label = (0, react_1.useMemo)(() => {
        if (!isForCustomer) {
            return isCustomerMessage
                ? translate('activity-feed_visible_customer')
                : translate('activity-feed_visible_freighthub');
        }
        return translate('activity-feed_message_freighthub');
    }, [isCustomerMessage, isForCustomer, translate]);
    const saveMessage = (newMessage) => {
        const content = saveMessageToStorage(newMessage);
        setDebounceHint(translate('activity-feed_message_saved'));
        setDebounceHintDate((0, moment_1.default)(content.timestamp));
    };
    const deleteMessage = () => {
        deleteMessageFromStorage();
        setDebounceHint('');
        setDebounceHintDate(null);
    };
    const onSaveDebouncedMessage = (0, react_1.useCallback)((0, lodash_es_1.debounce)((newMessage) => {
        setDebounceHint(translate('activity-feed_message_saving'));
        if (newMessage.length === 0) {
            deleteMessage();
        }
        else {
            saveMessage(newMessage);
        }
    }, debounceDelay), []);
    const onChangeMessage = (value) => {
        if (value.length < maxMessageLength) {
            setMessage(value);
            setDebounceHint(translate('activity-feed_message_writing'));
            setDebounceHintDate(null);
            onSaveDebouncedMessage(value);
        }
        else {
            setDebounceHint(translate('activity-feed_message_too_large'));
        }
    };
    const autosaveTimeLabel = debounceHintDate?.format(`[${translate('activity-feed_message_auto_save')}] h:mm a, ddd D.MM.YY`) ?? '';
    const handleSendMessage = () => {
        if (message.length === 0)
            return;
        onSendMessage(message, messageType, () => {
            setMessage('');
            deleteMessage();
        });
    };
    const handleToggleMessageType = () => {
        setMessageType((prevState) => prevState === MessageType.Communication ? MessageType.Message : MessageType.Communication);
    };
    return ((0, jsx_runtime_1.jsxs)("div", { className: classes.root, children: [(0, jsx_runtime_1.jsxs)("div", { className: classes.container, children: [(0, jsx_runtime_1.jsx)("div", { className: classes.avatarWrapper, children: (0, jsx_runtime_1.jsx)(silhouette_core_1.Tooltip, { title: userName, children: (0, jsx_runtime_1.jsx)("span", { children: (0, jsx_runtime_1.jsx)(Avatar_1.Avatar, { avatarType: avatarType, avatar: avatar, userType: (0, utils_1.isShipUserID)(user?._id) ? 'customer' : 'fortie', fullName: userName, size: "large" }) }) }) }), (0, jsx_runtime_1.jsxs)("div", { className: classes.inputBoxContainer, children: [(0, jsx_runtime_1.jsxs)("div", { className: classes.inputBoxInnerContainer, children: [(0, jsx_runtime_1.jsx)(silhouette_core_1.Input, { disableUnderline: true, fullWidth: true, value: message, placeholder: placeholder, onChange: (e) => onChangeMessage(e.target.value), multiline: true, className: classes.inputBox, inputProps: {
                                            'aria-label': label,
                                            'data-e2e-id': 'message_box-input',
                                        } }), (0, jsx_runtime_1.jsxs)("div", { className: classes.actionsContainer, children: [isInternalActivitiesEnabled && ((0, jsx_runtime_1.jsx)(silhouette_core_1.FormControlLabel, { control: (0, jsx_runtime_1.jsx)(silhouette_core_1.Checkbox, { color: "primary", checked: messageType === 'communication', onChange: handleToggleMessageType }), label: translate('activity-feed_message_send_as_internal_note'), className: classes.internalNote })), (0, jsx_runtime_1.jsx)("div", { className: !isForCustomer ? classes.hintText : classes.label, children: label }), uploadButton, (0, jsx_runtime_1.jsx)(silhouette_core_1.Button, { variant: "contained", disabled: isLoading, onClick: handleSendMessage, "data-e2e-id": "message_box-send_button", children: isLoading
                                                    ? translate('activity-feed_message_button_loading')
                                                    : translate('activity-feed_message_button_send') })] })] }), (0, jsx_runtime_1.jsxs)("div", { className: classes.hintContainer, "data-testid": "autosave-hint", children: [(0, jsx_runtime_1.jsx)(silhouette_core_1.Typography, { variant: "body2", color: "textSecondary", className: classes.hintText, children: debounceHint }), (0, jsx_runtime_1.jsx)(silhouette_core_1.Typography, { variant: "body2", color: "textSecondary", className: classes.hintText, children: autosaveTimeLabel })] })] })] }), uploadDialog] }));
};
exports.LeanMessageBox = LeanMessageBox;
const MessageBox = ({ uploadDocumentButton }) => {
    const { state: { user }, } = (0, frontend_infra_auth_1.useAuth)();
    const { mainEntity: entity, isInternal } = (0, ActivityFeedContext_1.useActivityFeedState)();
    const { isEnabled } = (0, frontend_infra_unleash_1.useUnleashFeatureFlags)();
    const useFeedV2MessageEndpoint = isEnabled(featureFlagKeys_1.UnleashFeatureFlagKeys.FEED_V2_MESSAGE_ENDPOINT) ?? false;
    const { postMessage } = (0, postMessageApi_1.usePostMessageApi)(entity.id);
    const { postFeedV2Message } = (0, postFeedV2MessageApi_1.useFeedV2PostMessageApi)();
    const { sendEvent } = (0, useAnalytics_1.useAnalytics)();
    const { logError } = (0, LoggerContext_1.useLoggerContext)();
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const userInfo = user;
    const avatarType = userInfo.thumbnailUrl ? Avatar_1.AvatarType.THUMBNAIL_AVATAR : Avatar_1.AvatarType.INITIALS_AVATAR;
    const userName = userInfo?.name || `${userInfo?.firstName} ${userInfo?.lastName}`;
    const avatar = userInfo.thumbnailUrl || userName;
    const userID = userInfo?._id;
    const messageStore = (0, react_1.useMemo)(() => new MessagesCollectionStore_1.MessagesCollectionStore(), []);
    const messageStoreCollectionID = userID ?? (0, lodash_es_1.uniqueId)('collection_');
    const isDocumentsUploadEnabled = entity.type !== 'booking';
    const handleOnSaveMessageToStorage = (message) => {
        return messageStore.saveMessageToCollection({
            messageID: entity.id,
            message,
            collectionID: messageStoreCollectionID,
        });
    };
    const getFeedGroupName = (entityID) => {
        if (entityID.startsWith('B')) {
            return feed_v2_models_1.FeedGroupName.booking;
        }
        if (entityID.startsWith('S')) {
            return feed_v2_models_1.FeedGroupName.shipment;
        }
        return undefined;
    };
    const handleSendMessage = async (message, type, onSuccess) => {
        sendEvent('activity-feed-message');
        setIsLoading(true);
        try {
            if (type === MessageType.Communication || useFeedV2MessageEndpoint) {
                await postFeedV2Message({
                    entityID: entity.id,
                    entityType: getFeedGroupName(entity.id),
                    message,
                    visibility: type === MessageType.Communication ? feed_v2_models_1.ActivityVisibility.internal : feed_v2_models_1.ActivityVisibility.public,
                });
            }
            else {
                await postMessage({ content: message, type, userId: userID });
            }
            onSuccess();
        }
        catch (error) {
            if (error instanceof Error) {
                logError(error);
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    return ((0, jsx_runtime_1.jsx)(exports.LeanMessageBox, { storedMessage: messageStore.getMessage({ collectionID: messageStoreCollectionID, messageID: entity.id }), saveMessageToStorage: handleOnSaveMessageToStorage, avatarType: avatarType, avatar: avatar, userName: userName, isForCustomer: !isInternal, isLoading: isLoading, deleteMessageFromStorage: () => messageStore.removeMessageFromCollection({
            collectionID: messageStoreCollectionID,
            messageID: entity.id,
        }), onSendMessage: handleSendMessage, isInternalActivitiesEnabled: isInternal, isDocumentsUploadEnabled: isDocumentsUploadEnabled, uploadDocumentButton: uploadDocumentButton }));
};
exports.MessageBox = MessageBox;
//# sourceMappingURL=MessageBox.js.map